<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="min-w-md flex flex-col overflow-y-auto w-full">
        <p class="font-semibold text-lg">
          {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.variationOrder') }}
        </p>

        <p class="font-semibold">
          {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.description') }}
        </p>

        <p>
          {{ variationOrder.description }}
        </p>

        <p
          v-if="variationOrder.notes"
          class="font-semibold mt-2"
        >
          {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.notes') }}
        </p>

        <div
          v-if="variationOrder.notes"
        >
          {{ variationOrder.notes }}
        </div>

        <form
          action=""
          class="flex flex-col space-y-4 mt-4"
          @submit.prevent=""
        >
          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.label.dateReceived') }}

            <AppInput
              v-model="noticeForm.form.date_received"
              name="date_received"
              type="date"
              class="form-input"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.label.to') }}

            <AppInput
              v-model="noticeForm.form.to"
              name="to"
              type="text"
              class="form-input"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.label.from') }}

            <AppInput
              v-model="noticeForm.form.from"
              name="from"
              class="form-input"
              type="text"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.label.description') }}

            <textarea
              v-model="noticeForm.form.description"
              name="description"
              class="form-input resize-none"
              type="text"
            />

            <span
              v-if="noticeForm.getErrors('description')"
              class="text-red-600 text-sm"
            >{{ noticeForm.getErrors('description') }}</span>
          </label>

          <label class="flex justify-between">
            {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.label.responseRequired') }}

            <input
              v-model="noticeForm.form.response_required"
              name="response_required"
              type="checkbox"
              class="form-checkbox"
              true-value="1"
              false-value="0"
            >
          </label>

          <FileUpload
            v-model="noticeForm.form.media"
            multiple
            :error="noticeForm.getErrors('media.0')"
            @change="addFile"
          />

          <div
            v-if="filesUploaded.length"
            class="overflow-x-auto max-w-sm flex"
          >
            <UploadedFileDisplay
              v-for="(file, i) in filesUploaded"
              :key="`${file.fileName}-${i}`"
              :file="file"
              :error="noticeForm.getErrors(`media.${i}`)"
              @delete="removeFile(i)"
            />
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          data-test="apply-filter"
          :disabled="storeNoticeAction.is(States.LOADING)"
          @click="storeNotice"
        >
          {{ $t('HeadContract.VariationsSchedule.RegisterNoticeModal.button.registerNotice') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, dateForInput } from '@/helpers';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';

const emit = defineEmits<{
  (e: 'close');
  (e: 'priced');
}>();

const props = defineProps<{
  variationOrderId: Id;
}>();

const variationsOrdersStore = useVariationOrdersStore();

const variationOrder = computed(() => {
  return variationsOrdersStore.models.find(props.variationOrderId);
});

const noticeForm = useFormData({
  date_received: dateForDateInput(new Date()),
  to: '',
  from: '',
  description: '',
  response_required: 0,
  media: [],
});

const storeNoticeAction = useStoreApiAction(variationsOrdersStore.storeNotice);

const storeNotice = () => {
  storeNoticeAction.request(props.variationOrderId, noticeForm.toFormDataObject()).then((data) => {
    Swal.fire({
      icon: 'success',
      title: 'Notice Registered',
    }).then(() => {
      emit('close');
    });
  }).catch((error) => {
    noticeForm.setErrors(error.data);
  });
};

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (file, index) => {
  noticeForm.form.media.splice(index, 1);
});
</script>

<style scoped></style>
